<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ContactCard from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactCard.vue';
import ContactsTable from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactTable.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({ 
  contacts: { type: Array, required: true },
  totalCount: { type: Number, default: 0 }, 
  currentPage: { type: Number, default: 1 }, 
  pageSize: { type: Number},
  isLoading: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: true },
});

const emit = defineEmits(['pageChange', 'loadMore', 'sendMessage']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);

const intersectionObserverOptions = {
  root: null,
  rootMargin: '200px',
  threshold: 0.2,
};

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'), { type: 'success' });
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`), { type: 'error' });
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`), { type: 'error' });
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data, { type: 'error' });
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`), { type: 'error' });
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

// Handle page change for table view
const handlePageChange = (newPageIndex) => {
  const pageNumber = newPageIndex + 1;
  emit('pageChange', pageNumber);
};

const loadMoreContacts = () => {
  if (props.isLoading || !props.hasMore) {
    return;
  }
  emit('loadMore');
};
</script>

<template>
  <div class="flex flex-col gap-4 mt-4">
    <!-- Desktop Table View -->
    <div class="hidden xl:block">
      <ContactsTable
        :contacts="contacts"
        :is-loading="isUpdating"
        :page-index="currentPage - 1"
        :total-count="totalCount"
        :page-size="pageSize"
        @pageChange="handlePageChange"
        @sendMessage="emit('sendMessage', $event)"
        @showContact="onClickViewDetails"
      />
    </div>

    <!-- Mobile/Tablet Card View with Infinite Scroll -->
    <div class="xl:hidden">
      <div class="h-[calc(100vh-200px)] overflow-y-auto scrollbar-none">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ContactCard
            v-for="contact in contacts"
            :key="contact.id"
            :contact="contact"
            :show-action="true"
            @sendMessage="emit('sendMessage', $event?.id ?? $event)"
            @showContact="onClickViewDetails"
          />
        </div>

        <div v-if="isLoading" class="flex items-center justify-center py-4">
          <div class="flex items-center gap-2 text-sm text-slate-500">
            <Spinner />
          </div>
        </div>
        <div
          v-else-if="!hasMore && contacts.length > 0"
          class="p-4 text-center text-slate-400 dark:text-slate-300"
        >
          Không còn liên hệ nào để tải
        </div>
        <IntersectionObserver
          v-else
          :options="intersectionObserverOptions"
          @observed="loadMoreContacts"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Sử dụng Tailwind utility class */
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-none::-webkit-scrollbar {
  display: none;
}
</style>